import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET, PUT } from "../../services/axios.service";
import { RootState } from "../../app/store";
import { ProcessRateProps, ProcessRateRow, ProcessRateOptionProps } from "../../types/processRate";
import { ProcessDefinitionProps } from "../../types/processDefinition";

type PartyInfo = {
  id: number;
  firm_name: string;
  name: string | null;
  phone: string | null;
  wp_phone: string | null;
  address: string | null;
  process_definitions: ProcessDefinitionProps[];
};

export interface ProcessRatesState {
  partyInfo: PartyInfo | null;
  definitionInfo: ProcessDefinitionProps | null;
  rates: ProcessRateProps[];
  subProcessOptions: ProcessRateOptionProps[];
  itemOptions: ProcessRateOptionProps[];
  loading: boolean;
  saving: boolean;
  error: string | null;
}

const initialState: ProcessRatesState = {
  partyInfo: null,
  definitionInfo: null,
  rates: [],
  subProcessOptions: [],
  itemOptions: [],
  loading: false,
  saving: false,
  error: null,
};

export const fetchPartyRates = createAsyncThunk(
  "processRates/fetchPartyRates",
  async (partyId: number) => {
    let response: any;
    await GET(`/v1/parties/${partyId}/rates`, {})
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const updatePartyRates = createAsyncThunk(
  "processRates/updatePartyRates",
  async (req: { partyId: number; process_definition_id: number; rates: ProcessRateRow[] }) => {
    let response: any;
    await PUT(`/v1/parties/${req.partyId}/rates`, {
      process_definition_id: req.process_definition_id,
      rates: req.rates,
    })
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const fetchInHouseRates = createAsyncThunk(
  "processRates/fetchInHouseRates",
  async (definitionId: number) => {
    let response: any;
    await GET(`/v1/process-definitions/${definitionId}/rates`, {})
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const updateInHouseRates = createAsyncThunk(
  "processRates/updateInHouseRates",
  async (req: { definitionId: number; rates: ProcessRateRow[] }) => {
    let response: any;
    await PUT(`/v1/process-definitions/${req.definitionId}/rates`, { rates: req.rates })
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const fetchRateSubProcessOptions = createAsyncThunk(
  "processRates/fetchSubProcessOptions",
  async (req: { process_definition_id: number }) => {
    let response: any;
    await GET(`/v1/process-orders-sub-processes`, { ...req })
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const fetchRateItemOptions = createAsyncThunk(
  "processRates/fetchItemOptions",
  async () => {
    let response: any;
    await GET(`/v1/process-orders-items`, {})
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const processRatesSlice = createSlice({
  name: "processRates",
  initialState,
  reducers: {
    resetProcessRates: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPartyRates.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchPartyRates.fulfilled, (state, action: any) => {
      state.loading = false;
      if (action.payload?.data?.status === "success") {
        state.partyInfo = action.payload.data.data.party_info;
        state.rates = action.payload.data.data.rates;
      } else {
        state.error = action?.payload?.data?.message || "Something went wrong.";
      }
    });
    builder.addCase(fetchPartyRates.rejected, (state, action: any) => {
      state.loading = false;
      state.error = action?.payload?.data?.message || "Something went wrong.";
    });

    builder.addCase(fetchInHouseRates.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchInHouseRates.fulfilled, (state, action: any) => {
      state.loading = false;
      if (action.payload?.data?.status === "success") {
        state.definitionInfo = action.payload.data.data.process_definition;
        state.rates = action.payload.data.data.rates;
      } else {
        state.error = action?.payload?.data?.message || "Something went wrong.";
      }
    });
    builder.addCase(fetchInHouseRates.rejected, (state, action: any) => {
      state.loading = false;
      state.error = action?.payload?.data?.message || "Something went wrong.";
    });

    builder.addCase(updatePartyRates.pending, (state) => {
      state.saving = true;
    });
    builder.addCase(updatePartyRates.fulfilled, (state) => {
      state.saving = false;
    });
    builder.addCase(updatePartyRates.rejected, (state) => {
      state.saving = false;
    });

    builder.addCase(updateInHouseRates.pending, (state) => {
      state.saving = true;
    });
    builder.addCase(updateInHouseRates.fulfilled, (state) => {
      state.saving = false;
    });
    builder.addCase(updateInHouseRates.rejected, (state) => {
      state.saving = false;
    });

    builder.addCase(fetchRateSubProcessOptions.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.subProcessOptions = (action.payload.data.data || []).map((p: any) => ({
          value: p.id,
          label: p.name,
        }));
      }
    });

    builder.addCase(fetchRateItemOptions.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.itemOptions = (action.payload.data.data || []).map((i: any) => ({
          value: i.id,
          label: i.name,
        }));
      }
    });
  },
});

export const { resetProcessRates } = processRatesSlice.actions;

export const getRatePartyInfo = (state: RootState) => state.processRates.partyInfo;
export const getRateDefinitionInfo = (state: RootState) => state.processRates.definitionInfo;
export const getProcessRates = (state: RootState) => state.processRates.rates;
export const getRateSubProcessOptions = (state: RootState) => state.processRates.subProcessOptions;
export const getRateItemOptions = (state: RootState) => state.processRates.itemOptions;
export const getProcessRatesLoading = (state: RootState) => state.processRates.loading;
export const getProcessRatesSaving = (state: RootState) => state.processRates.saving;

export default processRatesSlice.reducer;
